import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { changePanel, fetchRoofData } from '../RoofDataSlice/roofDataSlice';
import { decimetersToInches } from './util';
import { SETBACK } from '../../../constants';
import { RoofData } from '../RoofDataSlice/types';

export type DesignSettings = {
	setbacks: {
		nonGutter?: number;
		eaves?: number;
		ridges?: number;
		hips?: number;
		valleys?: number;
		rakes?: number;
		gutters?: number;
		obstructions?: number;
	};
	edgeTypeBasedSetbacks: boolean;
	openModal: boolean;
	applyingDesignSettings: boolean
}

const initialState = {
	data: {
		openModal: false,
		applyingDesignSettings: false,
		setbacks: { //setbacks in inches
			// Edges
			eaves: SETBACK.GUTTER,
			ridges: SETBACK.NONGUTTER,
			hips: SETBACK.NONGUTTER,
			valleys: SETBACK.NONGUTTER,
			rakes: SETBACK.GUTTER,
			gutters: SETBACK.GUTTER,
			obstructions: SETBACK.OBSTRUCTION,
			nonGutter: SETBACK.NONGUTTER,
		},
		useEdgeTypeBasedSetbacks: false
	}
};

export const designSettingSlice = createSlice({
	name: 'DesignSettings',
	initialState,
	reducers: {
		toggleSettingsModal(state, action: PayloadAction<{ open: boolean }>) {
			state.data.openModal = action.payload.open;
		},
		setSetback(state, action: PayloadAction<{ setback: Partial<DesignSettings['setbacks']> }>) {
			state.data.setbacks = {
				...state.data.setbacks,
				...action.payload.setback,
			};
		},
		setApplyingDesignSettings(state, action: PayloadAction<boolean>) {
			state.data.applyingDesignSettings = action.payload;
		}

	}, extraReducers: (builder,) => {
		builder
			.addCase(fetchRoofData.fulfilled, (state, action) => {
				const roofData = action.payload.roofData as RoofData;
				const setbacks = roofData.setbacks;

				state.data.setbacks = {
					eaves: decimetersToInches(setbacks?.eaves ?? 0),
					ridges: decimetersToInches(setbacks?.ridges ?? 0),
					hips: decimetersToInches(setbacks?.hips ?? 0),
					valleys: decimetersToInches(setbacks?.valleys ?? 0),
					rakes: decimetersToInches(setbacks?.rakes ?? 0),
					gutters: decimetersToInches(setbacks?.gutters ?? 0),
					obstructions: decimetersToInches(setbacks?.obstructions ?? 0),
					nonGutter: decimetersToInches(setbacks?.ridges?? 0),
				};
			
				state.data.useEdgeTypeBasedSetbacks = !!roofData.settings?.useEdgeTypeBasedSetbacks;
			})
			.addCase(changePanel.pending, (state, action) => {
				if (action.meta.arg.updateSettings) {
					state.data.applyingDesignSettings = true;
				}
			})
			.addCase(changePanel.rejected, (state) => {
				state.data.applyingDesignSettings = false;
			})
			.addCase(changePanel.fulfilled, (state, action) => {
				if (action.meta.arg.updateSettings) {
					state.data.openModal = false;
				}
				state.data.applyingDesignSettings = false;
				const roofData = action.payload.roofData as RoofData;
				const setbacks = roofData.setbacks;
				state.data.setbacks = {
					eaves: decimetersToInches(setbacks?.eaves ?? 0),
					ridges: decimetersToInches(setbacks?.ridges ?? 0),
					hips: decimetersToInches(setbacks?.hips ?? 0),
					valleys: decimetersToInches(setbacks?.valleys ?? 0),
					rakes: decimetersToInches(setbacks?.rakes ?? 0),
					gutters: decimetersToInches(setbacks?.gutters ?? 0),
					obstructions: decimetersToInches(setbacks?.obstructions ?? 0),
					nonGutter: decimetersToInches(setbacks?.ridges?? 0),
				};
				state.data.useEdgeTypeBasedSetbacks = !!roofData.settings?.useEdgeTypeBasedSetbacks;
			});
	}
});

export const { toggleSettingsModal, setSetback, setApplyingDesignSettings } = designSettingSlice.actions;
export default designSettingSlice.reducer;